import React, { useEffect, useState, useMemo } from "react"
import { get } from "lodash"

import { ForeignIsrael, ForeignTemplate } from "components/templates/foreign"
import { worldRankingState, worldRankingApi } from "components/actions/ranking"
import {
  useCallForeignData,
  useCallIsraelData,
  useForeignSymbolList,
  useGetSymbolDetailData,
  useIsraelSymbolList,
} from "components/actions/foreign"
import { Sort } from "utils/sort"
import { useIndicatorChartInitialize } from "components/atoms/chart/parts/useChartInitialize"
import { checkIsSp } from "utils/util"
import { useLodingTrigger, LoadingTriger } from "components/actions/loading"
import { useSelector } from "react-redux"
import { LoadStateType } from "redux/ducks/PageLoad"

export type CountryListType = {
  name: string
  icon: string
  code: string
  symbol?: {
    securityType: number
    exchange: Array<number>
  }
  selectList?: Array<{
    id: number
    name: string
    value: Array<number>
    division?: string
  }>
}
const countryList: { [t: string]: CountryListType } = {
  usa: {
    name: "米国",
    icon: "us",
    code: "US",
    selectList: [
      { id: 0, name: "全市場", value: [9, 10] },
      { id: 1, name: "NYSE", value: [9] },
      { id: 2, name: "NASDAQ", value: [10] },
    ],
  },
  hongkong: {
    name: "香港",
    icon: "hong-kong",
    code: "HK",
    selectList: [
      { id: 0, name: "全市場", value: [5, 6] },
      { id: 1, name: "香港", value: [5] },
      { id: 2, name: "香港GEM", value: [6] },
    ],
  },
  china: {
    name: "中国",
    icon: "china",
    code: "CN",
    selectList: [
      { id: 0, name: "全市場", value: [7, 8] },
      { id: 1, name: "上海A", value: [7], division: "CNHA" },
      { id: 2, name: "上海B", value: [7], division: "CNHB" },
      { id: 3, name: "深センA", value: [8], division: "CNZA" },
      { id: 4, name: "深センB", value: [8], division: "CNZB" },
    ],
  },
  korea: {
    name: "韓国",
    icon: "korea",
    code: "KR",
    selectList: [
      { id: 0, name: "全市場", value: [11, 12] },
      { id: 1, name: "韓国", value: [11] },
      { id: 2, name: "KOSDAQ", value: [12] },
    ],
  },
  taiwan: {
    name: "台湾",
    icon: "taiwan",
    code: "TW",
    selectList: [{ id: 0, name: "全市場", value: [16] }],
  },
  singapore: {
    name: "シンガポール",
    icon: "singapore",
    code: "SG",
    selectList: [{ id: 0, name: "全市場", value: [17] }],
  },
  thai: {
    name: "タイ",
    icon: "thailand",
    code: "TH",
    selectList: [{ id: 0, name: "全市場", value: [15] }],
  },
  malaysia: {
    name: "マレーシア",
    icon: "malaysia",
    code: "MY",
    selectList: [{ id: 0, name: "全市場", value: [19] }],
  },
  indonesia: {
    name: "インドネシア",
    icon: "indonesia",
    code: "ID",
    selectList: [{ id: 0, name: "全市場", value: [18] }],
  },
  philippine: {
    name: "フィリピン",
    icon: "philippines",
    code: "PH",
    selectList: [{ id: 0, name: "全市場", value: [20] }],
  },
  vietnam: {
    name: "ベトナム",
    icon: "vietnam",
    code: "VN",
    selectList: [
      { id: 0, name: "全市場", value: [13, 14] },
      { id: 1, name: "ホーチミン", value: [13] },
      { id: 2, name: "ハノイ", value: [14] },
    ],
  },
  israel: {
    name: "イスラエル",
    icon: "israel",
    code: "IL",
    symbol: { securityType: 2111, exchange: [2111] },
  },
}

export const Usa = () => {
  const [rankingData, changeRanking] = useState<worldRankingState>({
    exchangeTypeNum: 0,
    priceIncreaseList: [],
    priceReductionList: [],
    volumeList: [],
    priceIncreaseTime: "",
    priceReductionTime: "",
    volumeTime: "",
  })
  const [currentTab, changeCurrentTab] = useState(0)
  const [changeTabFlag, setChangeTabFlag] = useState(false)
  const canvasId = useIndicatorChartInitialize()
  const isPhone = useMemo(() => {
    return checkIsSp()
  }, [])
  const { loading, setLoading } = useLodingTrigger()
  const isPageLoading = useSelector(
    (state: { PageLoad: LoadStateType }) => state.PageLoad.load
  )
  const listData = useCallForeignData(
    countryList.usa.code,
    true,
    currentTab,
    loading && currentTab === 0,
    setLoading
  )
  const [foreignSymbolData, setSymbolData] = useGetSymbolDetailData(
    canvasId,
    changeTabFlag,
    setChangeTabFlag
  )
  const [
    symbolList,
    setPage,
    currentPage,
    selectList,
    setSelectList,
  ] = useForeignSymbolList(
    get(countryList.usa, "selectList"),
    currentTab,
    loading && currentTab === 1,
    setLoading
  )

  useEffect(() => {
    if (!listData) return
    !isPhone && setSymbolData(get(listData.index, "ResultList.0"))
  }, [listData])

  useEffect(() => {
    worldRankingApi({
      exchangeTypeNum: 0,
      priceIncrease: 140103,
      priceReduction: 140203,
      volume: 140303,
      changeRanking: changeRanking,
    })
  }, [currentTab])
  useEffect(() => {
    if (!isPageLoading) return
    worldRankingApi({
      exchangeTypeNum: 0,
      priceIncrease: 140103,
      priceReduction: 140203,
      volume: 140303,
      changeRanking: changeRanking,
    })
  }, [isPageLoading])

  return (
    <LoadingTriger.Provider value={{ loading, setLoading }}>
      <ForeignTemplate
        countryInfo={countryList.usa}
        rankingData={rankingData}
        changeRanking={changeRanking}
        rankingList={Sort.US_Ranking}
        callApi={worldRankingApi}
        listData={listData}
        symbolData={foreignSymbolData}
        setSymbolData={setSymbolData}
        symbolList={symbolList}
        currentSymbolPage={currentPage}
        symbolListChangePage={setPage}
        selectList={selectList}
        setSelectList={setSelectList}
        canvasId={canvasId}
        countryCode={countryList.usa.code}
        changeCurrentTab={changeCurrentTab}
        setChangeTabFlag={setChangeTabFlag}
      />
    </LoadingTriger.Provider>
  )
}

export const HongKong = () => {
  const [rankingData, changeRanking] = useState<worldRankingState>({
    exchangeTypeNum: 0,
    priceIncreaseList: [],
    priceReductionList: [],
    volumeList: [],
    priceIncreaseTime: "",
    priceReductionTime: "",
    volumeTime: "",
  })
  const [currentTab, changeCurrentTab] = useState(0)
  const [changeTabFlag, setChangeTabFlag] = useState(false)
  const canvasId = useIndicatorChartInitialize()
  const isPhone = useMemo(() => {
    return checkIsSp()
  }, [])
  const isPageLoading = useSelector(
    (state: { PageLoad: LoadStateType }) => state.PageLoad.load
  )

  const { loading, setLoading } = useLodingTrigger()
  const listData = useCallForeignData(
    countryList.hongkong.code,
    true,
    currentTab,
    loading && currentTab === 0,
    setLoading
  )
  const [foreignSymbolData, setSymbolData] = useGetSymbolDetailData(
    canvasId,
    changeTabFlag,
    setChangeTabFlag
  )
  const [
    symbolList,
    setPage,
    currentPage,
    selectList,
    setSelectList,
  ] = useForeignSymbolList(
    countryList.hongkong.selectList,
    currentTab,
    loading && currentTab === 1,
    setLoading
  )

  useEffect(() => {
    if (!listData) return
    !isPhone && setSymbolData(get(listData.index, "ResultList.0"))
  }, [listData])

  useEffect(() => {
    worldRankingApi({
      exchangeTypeNum: 0,
      priceIncrease: 110103,
      priceReduction: 110203,
      volume: 110303,
      changeRanking: changeRanking,
    })
  }, [currentTab])

  useEffect(() => {
    if (!isPageLoading) return
    worldRankingApi({
      exchangeTypeNum: 0,
      priceIncrease: 110103,
      priceReduction: 110203,
      volume: 110303,
      changeRanking: changeRanking,
    })
  }, [isPageLoading])

  return (
    <LoadingTriger.Provider value={{ loading, setLoading }}>
      <ForeignTemplate
        countryInfo={countryList.hongkong}
        rankingData={rankingData}
        changeRanking={changeRanking}
        rankingList={Sort.HongKong_Ranking}
        callApi={worldRankingApi}
        listData={listData}
        symbolData={foreignSymbolData}
        setSymbolData={setSymbolData}
        symbolList={symbolList}
        currentSymbolPage={currentPage}
        symbolListChangePage={setPage}
        selectList={selectList}
        setSelectList={setSelectList}
        canvasId={canvasId}
        countryCode={countryList.hongkong.code}
        changeCurrentTab={changeCurrentTab}
        setChangeTabFlag={setChangeTabFlag}
      />
    </LoadingTriger.Provider>
  )
}

export const China = () => {
  const [rankingData, changeRanking] = useState<worldRankingState>({
    exchangeTypeNum: 0,
    priceIncreaseList: [],
    priceReductionList: [],
    volumeList: [],
    priceIncreaseTime: "",
    priceReductionTime: "",
    volumeTime: "",
  })
  const [currentTab, changeCurrentTab] = useState(0)
  const [changeTabFlag, setChangeTabFlag] = useState(false)
  const canvasId = useIndicatorChartInitialize()
  const isPhone = useMemo(() => {
    return checkIsSp()
  }, [])
  const isPageLoading = useSelector(
    (state: { PageLoad: LoadStateType }) => state.PageLoad.load
  )

  const { loading, setLoading } = useLodingTrigger()
  const listData = useCallForeignData(
    countryList.china.code,
    true,
    currentTab,
    loading && currentTab === 0,
    setLoading
  )

  const [foreignSymbolData, setSymbolData] = useGetSymbolDetailData(
    canvasId,
    changeTabFlag,
    setChangeTabFlag
  )
  const [
    symbolList,
    setPage,
    currentPage,
    selectList,
    setSelectList,
  ] = useForeignSymbolList(
    countryList.china.selectList,
    currentTab,
    loading && currentTab === 1,
    setLoading
  )

  useEffect(() => {
    if (!listData) return
    !isPhone && setSymbolData(get(listData.index, "ResultList.0"))
  }, [listData])

  useEffect(() => {
    worldRankingApi({
      exchangeTypeNum: 0,
      priceIncrease: 100103,
      priceReduction: 100203,
      volume: 100303,
      changeRanking: changeRanking,
    })
  }, [currentTab])

  useEffect(() => {
    if (!isPageLoading) return
    worldRankingApi({
      exchangeTypeNum: 0,
      priceIncrease: 100103,
      priceReduction: 100203,
      volume: 100303,
      changeRanking: changeRanking,
    })
  }, [isPageLoading])

  return (
    <LoadingTriger.Provider value={{ loading, setLoading }}>
      <ForeignTemplate
        countryInfo={countryList.china}
        rankingData={rankingData}
        changeRanking={changeRanking}
        rankingList={Sort.China_Ranking}
        callApi={worldRankingApi}
        listData={listData}
        symbolData={foreignSymbolData}
        setSymbolData={setSymbolData}
        symbolList={symbolList}
        currentSymbolPage={currentPage}
        symbolListChangePage={setPage}
        selectList={selectList}
        setSelectList={setSelectList}
        canvasId={canvasId}
        countryCode={countryList.china.code}
        changeCurrentTab={changeCurrentTab}
        setChangeTabFlag={setChangeTabFlag}
      />
    </LoadingTriger.Provider>
  )
}

export const Korea = () => {
  const [rankingData, changeRanking] = useState<worldRankingState>({
    exchangeTypeNum: 0,
    priceIncreaseList: [],
    priceReductionList: [],
    volumeList: [],
    priceIncreaseTime: "",
    priceReductionTime: "",
    volumeTime: "",
  })
  const [currentTab, changeCurrentTab] = useState(0)
  const [changeTabFlag, setChangeTabFlag] = useState(false)
  const canvasId = useIndicatorChartInitialize()
  const isPhone = useMemo(() => {
    return checkIsSp()
  }, [])
  const isPageLoading = useSelector(
    (state: { PageLoad: LoadStateType }) => state.PageLoad.load
  )

  const { loading, setLoading } = useLodingTrigger()

  useEffect(() => {
    worldRankingApi({
      exchangeTypeNum: 0,
      priceIncrease: 120103,
      priceReduction: 120203,
      volume: 120303,
      changeRanking: changeRanking,
    })
  }, [currentTab])

  useEffect(() => {
    if (!isPageLoading) return
    worldRankingApi({
      exchangeTypeNum: 0,
      priceIncrease: 120103,
      priceReduction: 120203,
      volume: 120303,
      changeRanking: changeRanking,
    })
  }, [isPageLoading])

  const listData = useCallForeignData(
    countryList.korea.code,
    true,
    currentTab,
    loading && currentTab === 0,
    setLoading
  )
  const [foreignSymbolData, setSymbolData] = useGetSymbolDetailData(
    canvasId,
    changeTabFlag,
    setChangeTabFlag
  )
  const [
    symbolList,
    setPage,
    currentPage,
    selectList,
    setSelectList,
  ] = useForeignSymbolList(
    countryList.korea.selectList,
    currentTab,
    loading && currentTab === 1,
    setLoading
  )

  useEffect(() => {
    if (!listData) return
    !isPhone && setSymbolData(get(listData.index, "ResultList.0"))
  }, [listData])

  return (
    <LoadingTriger.Provider value={{ loading, setLoading }}>
      <ForeignTemplate
        countryInfo={countryList.korea}
        rankingData={rankingData}
        changeRanking={changeRanking}
        rankingList={Sort.Korea_Ranking}
        callApi={worldRankingApi}
        listData={listData}
        symbolData={foreignSymbolData}
        setSymbolData={setSymbolData}
        symbolList={symbolList}
        currentSymbolPage={currentPage}
        symbolListChangePage={setPage}
        selectList={selectList}
        setSelectList={setSelectList}
        canvasId={canvasId}
        countryCode={countryList.korea.code}
        changeCurrentTab={changeCurrentTab}
        setChangeTabFlag={setChangeTabFlag}
      />
    </LoadingTriger.Provider>
  )
}

export const Taiwan = () => {
  const [rankingData, changeRanking] = useState<worldRankingState>({
    exchangeTypeNum: 0,
    priceIncreaseList: [],
    priceReductionList: [],
    volumeList: [],
    priceIncreaseTime: "",
    priceReductionTime: "",
    volumeTime: "",
  })
  const [currentTab, changeCurrentTab] = useState(0)
  const [changeTabFlag, setChangeTabFlag] = useState(false)
  const { loading, setLoading } = useLodingTrigger()
  const isPhone = useMemo(() => {
    return checkIsSp()
  }, [])
  const isPageLoading = useSelector(
    (state: { PageLoad: LoadStateType }) => state.PageLoad.load
  )

  const canvasId = useIndicatorChartInitialize()
  const listData = useCallForeignData(
    countryList.taiwan.code,
    true,
    currentTab,
    loading && currentTab === 0,
    setLoading
  )
  const [foreignSymbolData, setSymbolData] = useGetSymbolDetailData(
    canvasId,
    changeTabFlag,
    setChangeTabFlag
  )
  const [
    symbolList,
    setPage,
    currentPage,
    selectList,
    setSelectList,
  ] = useForeignSymbolList(
    countryList.taiwan.selectList,
    currentTab,
    loading && currentTab === 1,
    setLoading
  )

  useEffect(() => {
    if (!listData) return
    !isPhone && setSymbolData(get(listData.index, "ResultList.0"))
  }, [listData])

  useEffect(() => {
    worldRankingApi({
      exchangeTypeNum: 0,
      priceIncrease: 160103,
      priceReduction: 160203,
      volume: 160303,
      changeRanking: changeRanking,
    })
  }, [currentTab])

  useEffect(() => {
    if (!isPageLoading) return
    worldRankingApi({
      exchangeTypeNum: 0,
      priceIncrease: 160103,
      priceReduction: 160203,
      volume: 160303,
      changeRanking: changeRanking,
    })
  }, [isPageLoading])

  return (
    <LoadingTriger.Provider value={{ loading, setLoading }}>
      <ForeignTemplate
        countryInfo={countryList.taiwan}
        rankingData={rankingData}
        changeRanking={changeRanking}
        rankingList={Sort.Taiwan_Ranking}
        callApi={worldRankingApi}
        listData={listData}
        symbolData={foreignSymbolData}
        setSymbolData={setSymbolData}
        symbolList={symbolList}
        currentSymbolPage={currentPage}
        symbolListChangePage={setPage}
        selectList={selectList}
        setSelectList={setSelectList}
        canvasId={canvasId}
        countryCode={countryList.taiwan.code}
        changeCurrentTab={changeCurrentTab}
        setChangeTabFlag={setChangeTabFlag}
      />
    </LoadingTriger.Provider>
  )
}

export const Singapore = () => {
  const [rankingData, changeRanking] = useState<worldRankingState>({
    exchangeTypeNum: 0,
    priceIncreaseList: [],
    priceReductionList: [],
    volumeList: [],
    priceIncreaseTime: "",
    priceReductionTime: "",
    volumeTime: "",
  })
  const [currentTab, changeCurrentTab] = useState(0)
  const [changeTabFlag, setChangeTabFlag] = useState(false)
  const canvasId = useIndicatorChartInitialize()
  const { loading, setLoading } = useLodingTrigger()
  const isPhone = useMemo(() => {
    return checkIsSp()
  }, [])
  const isPageLoading = useSelector(
    (state: { PageLoad: LoadStateType }) => state.PageLoad.load
  )

  const listData = useCallForeignData(
    countryList.singapore.code,
    true,
    currentTab,
    loading && currentTab === 0,
    setLoading
  )
  useEffect(() => {
    if (!listData) return
    !isPhone && setSymbolData(get(listData.index, "ResultList.0"))
  }, [listData])

  const [foreignSymbolData, setSymbolData] = useGetSymbolDetailData(
    canvasId,
    changeTabFlag,
    setChangeTabFlag
  )
  const [
    symbolList,
    setPage,
    currentPage,
    selectList,
    setSelectList,
  ] = useForeignSymbolList(
    countryList.singapore.selectList,
    currentTab,
    loading && currentTab === 1,
    setLoading
  )

  useEffect(() => {
    worldRankingApi({
      exchangeTypeNum: 0,
      priceIncrease: 170103,
      priceReduction: 170203,
      volume: 170303,
      changeRanking: changeRanking,
    })
  }, [currentTab])

  useEffect(() => {
    if (!isPageLoading) return
    worldRankingApi({
      exchangeTypeNum: 0,
      priceIncrease: 170103,
      priceReduction: 170203,
      volume: 170303,
      changeRanking: changeRanking,
    })
  }, [isPageLoading])

  return (
    <LoadingTriger.Provider value={{ loading, setLoading }}>
      <ForeignTemplate
        countryInfo={countryList.singapore}
        rankingData={rankingData}
        changeRanking={changeRanking}
        rankingList={Sort.Singapore_Ranking}
        callApi={worldRankingApi}
        listData={listData}
        symbolData={foreignSymbolData}
        setSymbolData={setSymbolData}
        symbolList={symbolList}
        currentSymbolPage={currentPage}
        symbolListChangePage={setPage}
        selectList={selectList}
        setSelectList={setSelectList}
        canvasId={canvasId}
        countryCode={countryList.singapore.code}
        changeCurrentTab={changeCurrentTab}
        setChangeTabFlag={setChangeTabFlag}
      />
    </LoadingTriger.Provider>
  )
}

export const Thai = () => {
  const [rankingData, changeRanking] = useState<worldRankingState>({
    exchangeTypeNum: 0,
    priceIncreaseList: [],
    priceReductionList: [],
    volumeList: [],
    priceIncreaseTime: "",
    priceReductionTime: "",
    volumeTime: "",
  })
  const [currentTab, changeCurrentTab] = useState(0)
  const [changeTabFlag, setChangeTabFlag] = useState(false)
  const canvasId = useIndicatorChartInitialize()
  const { loading, setLoading } = useLodingTrigger()
  const isPhone = useMemo(() => {
    return checkIsSp()
  }, [])
  const isPageLoading = useSelector(
    (state: { PageLoad: LoadStateType }) => state.PageLoad.load
  )

  const listData = useCallForeignData(
    countryList.thai.code,
    true,
    currentTab,
    loading && currentTab === 0,
    setLoading
  )
  const [foreignSymbolData, setSymbolData] = useGetSymbolDetailData(
    canvasId,
    changeTabFlag,
    setChangeTabFlag
  )
  const [
    symbolList,
    setPage,
    currentPage,
    selectList,
    setSelectList,
  ] = useForeignSymbolList(
    countryList.thai.selectList,
    currentTab,
    loading && currentTab === 1,
    setLoading
  )

  useEffect(() => {
    if (!listData) return
    !isPhone && setSymbolData(get(listData.index, "ResultList.0"))
  }, [listData])

  useEffect(() => {
    worldRankingApi({
      exchangeTypeNum: 0,
      priceIncrease: 150103,
      priceReduction: 150203,
      volume: 150303,
      changeRanking: changeRanking,
    })
  }, [currentTab])

  useEffect(() => {
    if (!isPageLoading) return
    worldRankingApi({
      exchangeTypeNum: 0,
      priceIncrease: 150103,
      priceReduction: 150203,
      volume: 150303,
      changeRanking: changeRanking,
    })
  }, [isPageLoading])

  return (
    <LoadingTriger.Provider value={{ loading, setLoading }}>
      <ForeignTemplate
        countryInfo={countryList.thai}
        rankingData={rankingData}
        changeRanking={changeRanking}
        rankingList={Sort.Thailand_Ranking}
        callApi={worldRankingApi}
        listData={listData}
        symbolData={foreignSymbolData}
        setSymbolData={setSymbolData}
        symbolList={symbolList}
        currentSymbolPage={currentPage}
        symbolListChangePage={setPage}
        selectList={selectList}
        setSelectList={setSelectList}
        canvasId={canvasId}
        countryCode={countryList.thai.code}
        changeCurrentTab={changeCurrentTab}
        setChangeTabFlag={setChangeTabFlag}
      />
    </LoadingTriger.Provider>
  )
}

export const Malaysia = () => {
  const [rankingData, changeRanking] = useState<worldRankingState>({
    exchangeTypeNum: 0,
    priceIncreaseList: [],
    priceReductionList: [],
    volumeList: [],
    priceIncreaseTime: "",
    priceReductionTime: "",
    volumeTime: "",
  })
  const [currentTab, changeCurrentTab] = useState(0)
  const [changeTabFlag, setChangeTabFlag] = useState(false)
  const canvasId = useIndicatorChartInitialize()
  const isPhone = useMemo(() => {
    return checkIsSp()
  }, [])
  const isPageLoading = useSelector(
    (state: { PageLoad: LoadStateType }) => state.PageLoad.load
  )

  const { loading, setLoading } = useLodingTrigger()
  const listData = useCallForeignData(
    countryList.malaysia.code,
    true,
    currentTab,
    loading && currentTab === 0,
    setLoading
  )
  const [foreignSymbolData, setSymbolData] = useGetSymbolDetailData(
    canvasId,
    changeTabFlag,
    setChangeTabFlag
  )
  const [
    symbolList,
    setPage,
    currentPage,
    selectList,
    setSelectList,
  ] = useForeignSymbolList(
    countryList.malaysia.selectList,
    currentTab,
    loading && currentTab === 1,
    setLoading
  )

  useEffect(() => {
    if (!listData) return
    !isPhone && setSymbolData(get(listData.index, "ResultList.0"))
  }, [listData])

  useEffect(() => {
    worldRankingApi({
      exchangeTypeNum: 0,
      priceIncrease: 190103,
      priceReduction: 190203,
      volume: 190303,
      changeRanking: changeRanking,
    })
  }, [currentTab])

  useEffect(() => {
    if (!isPageLoading) return
    worldRankingApi({
      exchangeTypeNum: 0,
      priceIncrease: 190103,
      priceReduction: 190203,
      volume: 190303,
      changeRanking: changeRanking,
    })
  }, [isPageLoading])

  return (
    <LoadingTriger.Provider value={{ loading, setLoading }}>
      <ForeignTemplate
        countryInfo={countryList.malaysia}
        rankingData={rankingData}
        changeRanking={changeRanking}
        rankingList={Sort.Malaysia_Ranking}
        callApi={worldRankingApi}
        listData={listData}
        symbolData={foreignSymbolData}
        setSymbolData={setSymbolData}
        symbolList={symbolList}
        currentSymbolPage={currentPage}
        symbolListChangePage={setPage}
        selectList={selectList}
        setSelectList={setSelectList}
        canvasId={canvasId}
        countryCode={countryList.malaysia.code}
        changeCurrentTab={changeCurrentTab}
        setChangeTabFlag={setChangeTabFlag}
      />
    </LoadingTriger.Provider>
  )
}

export const Indonesia = () => {
  const [rankingData, changeRanking] = useState<worldRankingState>({
    exchangeTypeNum: 0,
    priceIncreaseList: [],
    priceReductionList: [],
    volumeList: [],
    priceIncreaseTime: "",
    priceReductionTime: "",
    volumeTime: "",
  })
  const [currentTab, changeCurrentTab] = useState(0)
  const [changeTabFlag, setChangeTabFlag] = useState(false)
  const canvasId = useIndicatorChartInitialize()
  const { loading, setLoading } = useLodingTrigger()
  const isPhone = useMemo(() => {
    return checkIsSp()
  }, [])
  const isPageLoading = useSelector(
    (state: { PageLoad: LoadStateType }) => state.PageLoad.load
  )

  const listData = useCallForeignData(
    countryList.indonesia.code,
    true,
    currentTab,
    loading && currentTab === 0,
    setLoading
  )
  const [foreignSymbolData, setSymbolData] = useGetSymbolDetailData(
    canvasId,
    changeTabFlag,
    setChangeTabFlag
  )
  const [
    symbolList,
    setPage,
    currentPage,
    selectList,
    setSelectList,
  ] = useForeignSymbolList(
    countryList.indonesia.selectList,
    currentTab,
    loading && currentTab === 1,
    setLoading
  )

  useEffect(() => {
    worldRankingApi({
      exchangeTypeNum: 0,
      priceIncrease: 180103,
      priceReduction: 180203,
      volume: 180303,
      changeRanking: changeRanking,
    })
  }, [currentTab])

  useEffect(() => {
    if (!listData) return
    !isPhone && setSymbolData(get(listData.index, "ResultList.0"))
  }, [listData])

  useEffect(() => {
    if (!isPageLoading) return
    worldRankingApi({
      exchangeTypeNum: 0,
      priceIncrease: 180103,
      priceReduction: 180203,
      volume: 180303,
      changeRanking: changeRanking,
    })
  }, [isPageLoading])

  return (
    <LoadingTriger.Provider value={{ loading, setLoading }}>
      <ForeignTemplate
        countryInfo={countryList.indonesia}
        rankingData={rankingData}
        changeRanking={changeRanking}
        rankingList={Sort.Indonesia_Ranking}
        callApi={worldRankingApi}
        listData={listData}
        symbolData={foreignSymbolData}
        setSymbolData={setSymbolData}
        symbolList={symbolList}
        currentSymbolPage={currentPage}
        symbolListChangePage={setPage}
        selectList={selectList}
        setSelectList={setSelectList}
        canvasId={canvasId}
        countryCode={countryList.indonesia.code}
        changeCurrentTab={changeCurrentTab}
        setChangeTabFlag={setChangeTabFlag}
      />
    </LoadingTriger.Provider>
  )
}

export const Philippine = () => {
  const [rankingData, changeRanking] = useState<worldRankingState>({
    exchangeTypeNum: 0,
    priceIncreaseList: [],
    priceReductionList: [],
    volumeList: [],
    priceIncreaseTime: "",
    priceReductionTime: "",
    volumeTime: "",
  })
  const [currentTab, changeCurrentTab] = useState(0)
  const [changeTabFlag, setChangeTabFlag] = useState(false)
  const canvasId = useIndicatorChartInitialize()
  const isPhone = useMemo(() => {
    return checkIsSp()
  }, [])
  const isPageLoading = useSelector(
    (state: { PageLoad: LoadStateType }) => state.PageLoad.load
  )

  const { loading, setLoading } = useLodingTrigger()
  const listData = useCallForeignData(
    countryList.philippine.code,
    true,
    currentTab,
    loading && currentTab === 0,
    setLoading
  )
  const [foreignSymbolData, setSymbolData] = useGetSymbolDetailData(
    canvasId,
    changeTabFlag,
    setChangeTabFlag
  )
  const [
    symbolList,
    setPage,
    currentPage,
    selectList,
    setSelectList,
  ] = useForeignSymbolList(
    countryList.philippine.selectList,
    currentTab,
    loading && currentTab === 1,
    setLoading
  )

  useEffect(() => {
    worldRankingApi({
      exchangeTypeNum: 0,
      priceIncrease: 200103,
      priceReduction: 200203,
      volume: 200303,
      changeRanking: changeRanking,
    })
  }, [currentTab])

  useEffect(() => {
    if (!listData) return
    !isPhone && setSymbolData(get(listData.index, "ResultList.0"))
  }, [listData])

  useEffect(() => {
    if (!isPageLoading) return
    worldRankingApi({
      exchangeTypeNum: 0,
      priceIncrease: 200103,
      priceReduction: 200203,
      volume: 200303,
      changeRanking: changeRanking,
    })
  }, [isPageLoading])

  return (
    <LoadingTriger.Provider value={{ loading, setLoading }}>
      <ForeignTemplate
        countryInfo={countryList.philippine}
        rankingData={rankingData}
        changeRanking={changeRanking}
        rankingList={Sort.Philippine_Ranking}
        callApi={worldRankingApi}
        listData={listData}
        symbolData={foreignSymbolData}
        setSymbolData={setSymbolData}
        symbolList={symbolList}
        currentSymbolPage={currentPage}
        symbolListChangePage={setPage}
        selectList={selectList}
        setSelectList={setSelectList}
        canvasId={canvasId}
        countryCode={countryList.philippine.code}
        changeCurrentTab={changeCurrentTab}
        setChangeTabFlag={setChangeTabFlag}
      />
    </LoadingTriger.Provider>
  )
}

export const Vietnam = () => {
  const [rankingData, changeRanking] = useState<worldRankingState>({
    exchangeTypeNum: 0,
    priceIncreaseList: [],
    priceReductionList: [],
    volumeList: [],
    priceIncreaseTime: "",
    priceReductionTime: "",
    volumeTime: "",
  })
  const [currentTab, changeCurrentTab] = useState(0)
  const [changeTabFlag, setChangeTabFlag] = useState(false)
  const canvasId = useIndicatorChartInitialize()
  const { loading, setLoading } = useLodingTrigger()
  const isPhone = useMemo(() => {
    return checkIsSp()
  }, [])
  const isPageLoading = useSelector(
    (state: { PageLoad: LoadStateType }) => state.PageLoad.load
  )

  const listData = useCallForeignData(
    countryList.vietnam.code,
    false,
    currentTab,
    loading && currentTab === 0,
    setLoading
  )
  const [foreignSymbolData, setSymbolData] = useGetSymbolDetailData(
    canvasId,
    changeTabFlag,
    setChangeTabFlag
  )
  const [
    symbolList,
    setPage,
    currentPage,
    selectList,
    setSelectList,
  ] = useForeignSymbolList(
    countryList.vietnam.selectList,
    currentTab,
    loading && currentTab === 1,
    setLoading
  )

  useEffect(() => {
    worldRankingApi({
      exchangeTypeNum: 0,
      priceIncrease: 130103,
      priceReduction: 130203,
      volume: 130303,
      changeRanking: changeRanking,
    })
  }, [currentTab])

  useEffect(() => {
    if (!listData) return
    !isPhone && setSymbolData(get(listData.index, "ResultList.0"))
  }, [listData])

  useEffect(() => {
    if (!isPageLoading) return
    worldRankingApi({
      exchangeTypeNum: 0,
      priceIncrease: 130103,
      priceReduction: 130203,
      volume: 130303,
      changeRanking: changeRanking,
    })
  }, [isPageLoading])

  return (
    <LoadingTriger.Provider value={{ loading, setLoading }}>
      <ForeignTemplate
        countryInfo={countryList.vietnam}
        rankingData={rankingData}
        changeRanking={changeRanking}
        rankingList={Sort.Vietnam_Ranking}
        callApi={worldRankingApi}
        listData={listData}
        symbolData={foreignSymbolData}
        setSymbolData={setSymbolData}
        symbolList={symbolList}
        currentSymbolPage={currentPage}
        symbolListChangePage={setPage}
        selectList={selectList}
        setSelectList={setSelectList}
        canvasId={canvasId}
        countryCode={countryList.vietnam.code}
        changeCurrentTab={changeCurrentTab}
        setChangeTabFlag={setChangeTabFlag}
      />
    </LoadingTriger.Provider>
  )
}

export const Israel = () => {
  const listData = useCallIsraelData()
  const [symbolList, setPage, currentPage] = useIsraelSymbolList(
    countryList.israel.symbol
  )
  return (
    <ForeignIsrael
      countryInfo={countryList.israel}
      listData={listData}
      symbolList={symbolList}
      symbolPage={currentPage}
      changeSymbolPage={(n: number) => setPage(n)}
    />
  )
}
